/**
 * Value Matching — Route configuration.
 *
 * This file is the single source of truth for everything routing in the
 * Value Matching domain:
 *
 *   - CAMPAIGN_CONFIG        — per-type metadata: slug, displayName, navSid (non-routing)
 *   - SLUG_TO_CAMPAIGN_TYPE  — reverse-lookup: URL slug → CampaignType enum
 *   - ROUTES                 — named route paths derived from slug constants
 *   - NAV_MODEL              — side-navigation links derived from CAMPAIGN_CONFIG
 *   - API_ENDPOINTS          — API base URLs for value matching operations
 *
 * To add a new campaign type: add a SLUG_* constant and one entry to CAMPAIGN_CONFIG.
 * To add a new page: add a route in routeTree.tsx.
 */

import { CampaignType } from 'src/@types/valueMatching/enums';

export const VALUE_MATCHING_BASE = '/value-matching';

// ============================================================================
// URL slugs — named constants for the :campaignType path segment
//
// These are the human-friendly URL segments used in browser addresses.
// They map to CampaignType enum values via SLUG_TO_CAMPAIGN_TYPE.
// ============================================================================

export const SLUG_NO_COST_EMI = 'no-cost-emi';
export const SLUG_CASHBACK = 'cashback';
export const SLUG_BUYBACK = 'buyback';
export const SLUG_DEAL = 'deal';
export const SLUG_INSTANT_BANK_DISCOUNT = 'instant-bank-discount';

// ============================================================================
// Campaign Config — campaign metadata (non-routing)
// ============================================================================

interface CampaignConfig {
  /** URL-friendly slug used as the :campaignType path segment */
  slug: string;
  /** Human-readable display name shown in the UI */
  displayName: string;
  /** i18n key for the nav side link */
  navSid: string;
}

export const CAMPAIGN_CONFIG: Record<CampaignType, CampaignConfig> = {
  [CampaignType.NO_COST_EMI]: {
    slug: SLUG_NO_COST_EMI,
    displayName: 'No Cost EMI',
    navSid: 'no_cost_emi_link_text',
  },
  [CampaignType.CASHBACK]: {
    slug: SLUG_CASHBACK,
    displayName: 'Prepaid Transaction Discount',
    navSid: 'prepaid_transaction_discount_link_text',
  },
  [CampaignType.BUYBACK]: {
    slug: SLUG_BUYBACK,
    displayName: 'Buyback',
    navSid: 'buyback_link_text',
  },
  [CampaignType.DEAL]: {
    slug: SLUG_DEAL,
    displayName: 'Deal',
    navSid: 'deal_link_text',
  },
  [CampaignType.INSTANT_BANK_DISCOUNT]: {
    slug: SLUG_INSTANT_BANK_DISCOUNT,
    displayName: 'Instant Bank Discount',
    navSid: 'instant_bank_discount_link_text',
  },
};

// ============================================================================
// SLUG_TO_CAMPAIGN_TYPE — reverse-lookup: URL slug → CampaignType enum
// ============================================================================

// eslint-disable-next-line no-secrets/no-secrets
/**
 * Reverse-lookup map: URL slug → CampaignType enum value.
 * Used by useFetchCampaigns to translate the :campaignType route param into the API enum.
 *
 * @example SLUG_TO_CAMPAIGN_TYPE['cashback'] === CampaignType.CASHBACK
 */
export const SLUG_TO_CAMPAIGN_TYPE: Partial<Record<string, CampaignType>> = (
  Object.entries(CAMPAIGN_CONFIG) as [CampaignType, CampaignConfig][]
).reduce<Record<string, CampaignType>>((acc, [campaignType, config]) => {
  acc[config.slug] = campaignType;
  return acc;
}, {});

// ============================================================================
// buildRoute — dynamic route builders (single source of truth)
//
// Components should use these functions instead of manually constructing
// route strings. Accepts runtime params (campaignType, campaignId) and
// returns the full path.
// ============================================================================

export const buildRoute = {
  /** Root: /value-matching */
  root: () => VALUE_MATCHING_BASE,
  /** List: /value-matching/:slug/list */
  list: (campaignType: CampaignType) =>
    `${VALUE_MATCHING_BASE}/${CAMPAIGN_CONFIG[campaignType].slug}/list`,
  /** Create: /value-matching/:slug/create */
  create: (campaignType: CampaignType) =>
    `${VALUE_MATCHING_BASE}/${CAMPAIGN_CONFIG[campaignType].slug}/create`,
  /** View: /value-matching/:slug/view/:campaignId/:version */
  view: (campaignType: CampaignType, campaignId: string, version: string) =>
    `${VALUE_MATCHING_BASE}/${CAMPAIGN_CONFIG[campaignType].slug}/view/${campaignId}/${version}`,
  /** Modify: /value-matching/:slug/modify/:campaignId/:version */
  modify: (campaignType: CampaignType, campaignId: string, version: string) =>
    `${VALUE_MATCHING_BASE}/${CAMPAIGN_CONFIG[campaignType].slug}/modify/${campaignId}/${version}`,
};

// ============================================================================
// ROUTES — static named routes derived from buildRoute (for static references)
// ============================================================================

export const ROUTES = {
  ROOT: VALUE_MATCHING_BASE,
  // No Cost EMI
  NO_COST_EMI: `${VALUE_MATCHING_BASE}/${SLUG_NO_COST_EMI}`,
  NO_COST_EMI_CREATE: buildRoute.create(CampaignType.NO_COST_EMI),
  NO_COST_EMI_LIST: buildRoute.list(CampaignType.NO_COST_EMI),
  // Prepaid Transaction Discount (CASHBACK)
  PREPAID_TRANSACTION_DISCOUNT: `${VALUE_MATCHING_BASE}/${SLUG_CASHBACK}`,
  PREPAID_TRANSACTION_DISCOUNT_CREATE: buildRoute.create(CampaignType.CASHBACK),
  PREPAID_TRANSACTION_DISCOUNT_LIST: buildRoute.list(CampaignType.CASHBACK),
  // Buyback
  BUYBACK: `${VALUE_MATCHING_BASE}/${SLUG_BUYBACK}`,
  BUYBACK_CREATE: buildRoute.create(CampaignType.BUYBACK),
  BUYBACK_LIST: buildRoute.list(CampaignType.BUYBACK),
  // Deal
  DEAL: `${VALUE_MATCHING_BASE}/${SLUG_DEAL}`,
  DEAL_CREATE: buildRoute.create(CampaignType.DEAL),
  DEAL_LIST: buildRoute.list(CampaignType.DEAL),
  // Instant Bank Discount
  INSTANT_BANK_DISCOUNT: `${VALUE_MATCHING_BASE}/${SLUG_INSTANT_BANK_DISCOUNT}`,
  INSTANT_BANK_DISCOUNT_CREATE: buildRoute.create(CampaignType.INSTANT_BANK_DISCOUNT),
  INSTANT_BANK_DISCOUNT_LIST: buildRoute.list(CampaignType.INSTANT_BANK_DISCOUNT),
} as const;

// ============================================================================
// API Endpoints — value matching backend URLs
// ============================================================================

export const API_ENDPOINTS = {
  VALUE_MATCHING_LIST_CAMPAIGNS: `/valuematching-api/v1/list-campaigns`,
  VALUE_MATCHING_CAMPAIGN_CONFIG: `/valuematching-api/v1/campaign-config`,
  VALUE_MATCHING_CREATE_CAMPAIGN: `/valuematching-api/v1/create-campaign`,
  VALUE_MATCHING_MODIFY_CAMPAIGN: `/valuematching-api/v1/modify-campaign`,
  VALUE_MATCHING_CAMPAIGN_DETAILS: `/valuematching-api/v1/campaign`,
  VALUE_MATCHING_CANCEL_CAMPAIGN: `/valuematching-api/v1/cancel-campaign`,
};

// ============================================================================
// Nav Model — derived from CAMPAIGN_CONFIG, links to the list page
// ============================================================================

export const NAV_MODEL = {
  links: (Object.entries(CAMPAIGN_CONFIG) as [CampaignType, CampaignConfig][]).map(
    ([campaignType, config]) => ({
      id: campaignType,
      to: `${VALUE_MATCHING_BASE}/${config.slug}/list`,
      sid: config.navSid,
    })
  ),
};
